import type { RouteLocationNormalized } from 'vue-router';
import { route } from 'ziggy-js';

import { useAbusePreventionStore } from '@/common/stores/abusePrevention';
import { useSubscriptionStore } from '@/common/stores/subscription';
import { axios, isShopConnectionRequiredError } from '@/helpers/axios';
import { useListingAuditQuotaModalStore } from '@/modules/listing-audit/stores/quotaModal';

const routes = [
  {
    path: '/listing-audit',
    name: 'listing-audit-index',
    component: () => import('@/modules/listing-audit/pages/Main.vue'),
    meta: {
      title: 'eRank — Listing Audit',
      metaTags: [
        {
          name: 'description',
          content: 'Get a detailed analysis of your Etsy listing and see how it compares to the competition.',
        },
        {
          page_slug: 'listing-audit-index',
        },
      ],
      requiresAuth: true,
    },
  },
  {
    path: '/listing-audit/:id',
    name: 'listing-audit',
    component: () => import('@/modules/listing-audit/pages/Entry.vue'),
    meta: {
      title: 'eRank — Listing Audit',
      metaTags: [
        {
          name: 'description',
          content: 'Get a detailed analysis of your Etsy listing and see how it compares to the competition.',
        },
        {
          page_slug: 'listing-audit',
        },
      ],
      requiresAuth: true,
    },
    beforeEnter: async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
      const listingId = to.params.id;

      if (!listingId || Array.isArray(listingId)) {
        return { name: 'listing-not-found' };
      }

      const abusePreventionStore = useAbusePreventionStore();
      if (abusePreventionStore.isShopConnectionRequired) {
        abusePreventionStore.openConnectShopModal('listing-audit');
        return from.matched.length > 0 ? false : { name: 'listing-audit-index' };
      }

      try {
        const response = await axios.get<{ success: boolean }>(
          route('api.listing-audit.check-status', { id: listingId }),
        );

        // Gate on the Listing Audit quota BEFORE the page loads, so a member without
        // remaining quota never lands on a blank audit screen behind the upgrade modal.
        const subscriptionStore = useSubscriptionStore();
        const listingAuditQuota = subscriptionStore.quotas?.listing_audit;

        if (listingAuditQuota && !listingAuditQuota.can_search && !response.data.success) {
          useListingAuditQuotaModalStore().open();
          return from.matched.length > 0 ? false : { name: 'dashboard' };
        }

        if (!response.data.success) {
          return { name: 'listing-not-found' };
        }

        return true;
      } catch (error) {
        if(error.response?.status === 400) {
          useListingAuditQuotaModalStore().open();
          return from.matched.length > 0 ? false : { name: 'dashboard' };
        }

        if (isShopConnectionRequiredError(error)) {
          abusePreventionStore.openConnectShopModal('listing-audit');
          return from.matched.length > 0 ? false : { name: 'listing-audit-index' };
        }
        return { name: 'listing-not-found' };
      }
    },
  },
  {
    path: '/listing-not-found',
    name: 'listing-not-found',
    component: () => import('@/modules/listing-audit/pages/ListingNotFound.vue'),
    meta: {
      title: 'eRank — Listing Not Found',
      requiresAuth: true,
    },
  },
];

export default routes;
